<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Buttons from '@/components/ui/Buttons.vue';
import ApplicationsList from '@/components/ApplicationsList.vue';
import SocialsList from '@/components/SocialsList.vue';
import { useMediaQuery } from '@vueuse/core';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const isDesktop = useMediaQuery('(min-width: 1024px)');
</script>
<template>
  <section
    :id="data.id"
    class="section section--hero-autocenter"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : '',
      spacingBottom ? `section--bottom-${spacingBottom}` : '',
    ]"
  >
    <div class="container">
      <div class="hero-autocenter">
        <div
          class="hero-autocenter__top"
          :style="{ 'background-image': 'url(' + data?.image?.src + ')' }"
        >
          <div class="hero-autocenter__text">
            <h2
              v-show="data?.title"
              class="mb-0 hero-autocenter__title h3 white"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="data?.title"
            ></h2>
            <Buttons
              v-if="data?.buttons && data?.buttons.length > 0"
              class="hero-autocenter__buttons"
              :template="'main'"
              :buttons="data?.buttons"
              :background-color="'black'"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              :buttons-prefix-class="'js-button-hero-autocenter'"
            />
          </div>
          <div
            class="hero-autocenter__info"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <div
              v-if="data?.address"
              class="hero-autocenter__address link-normal white"
              v-html="data?.address"
            ></div>
            <div
              v-if="data?.time"
              class="hero-autocenter__schedule link-small white"
              v-html="data?.time"
            ></div>
          </div>
          <ApplicationsList
            v-if="
              isDesktop && data?.applications && data?.applications.length > 0
            "
            :data="data?.applications"
            :view="'light'"
            :icon="'with-bg'"
            :template="'hero'"
            class="hero-autocenter__applications"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-hero-autocenter-application'"
          />
          <SocialsList
            v-if="data?.socials && data?.socials.length > 0"
            :data="data?.socials"
            :view="'light'"
            class="hero-autocenter__socials"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          />
        </div>
        <div class="hero-autocenter__bottom">
          <ul
            v-if="data?.services && data?.services.length > 0"
            class="m-0 p-0 hero-autocenter__services"
          >
            <template
              v-for="(servicesItem, servicesItemIndex) in data.services"
              :key="servicesItemIndex"
            >
              <li
                v-if="servicesItem?.title || servicesItem?.url"
                class="m-0 hero-autocenter__services-item"
                data-aos="fade-up"
                :data-aos-delay="
                  servicesItemIndex >= 1 ? 100 + 100 * servicesItemIndex : 100
                "
              >
                <CTagDetect
                  v-if="servicesItem?.url"
                  :to="servicesItem?.url"
                  class="hero-autocenter__services-item-wrap"
                >
                  <div
                    class="hero-autocenter__services-item-top"
                    :class="[
                      servicesItem?.description
                        ? 'hero-autocenter__services-item-top--with-description'
                        : '',
                    ]"
                  >
                    <NuxtImg
                      v-if="servicesItem?.image?.src"
                      :src="servicesItem?.image?.src"
                      :alt="servicesItem?.image?.alt"
                      :height="servicesItem?.image?.height"
                      :width="servicesItem?.image?.width"
                      class="hero-autocenter__services-item-image"
                      preload
                      fetchpriority="high"
                      loading="eager"
                    />
                    <div
                      v-if="servicesItem?.title"
                      class="hero-autocenter__services-item-title hero-autocenter__services-item-title--with-arrow"
                    >
                      <p
                        class="mb-0 hero-autocenter__services-item-title-value"
                        v-html="servicesItem?.title"
                      ></p>
                      <CIcon
                        name="arrow-red"
                        width="16"
                        height="16"
                        class="hero-autocenter__services-item-icon"
                      />
                    </div>
                  </div>
                  <div
                    v-if="servicesItem?.description"
                    class="hero-autocenter__services-item-description label"
                    v-html="servicesItem?.description"
                  ></div>
                </CTagDetect>
                <div v-else class="hero-autocenter__services-item-wrap">
                  <div
                    class="hero-autocenter__services-item-top"
                    :class="[
                      servicesItem?.description
                        ? 'hero-autocenter__services-item-top--with-description'
                        : '',
                    ]"
                  >
                    <NuxtImg
                      v-if="servicesItem?.image?.src"
                      :src="servicesItem?.image?.src"
                      :alt="servicesItem?.image?.alt"
                      :height="servicesItem?.image?.height"
                      :width="servicesItem?.image?.width"
                      class="hero-autocenter__services-item-image"
                      preload
                      fetchpriority="high"
                      loading="eager"
                    />
                    <div
                      v-if="servicesItem?.title"
                      class="hero-autocenter__services-item-title"
                    >
                      <p
                        class="mb-0 hero-autocenter__services-item-title-value"
                        v-html="servicesItem?.title"
                      ></p>
                    </div>
                  </div>
                  <div
                    v-if="servicesItem?.description"
                    class="hero-autocenter__services-item-description label"
                    v-html="servicesItem?.description"
                  ></div>
                </div>
              </li>
            </template>
          </ul>
          <ApplicationsList
            v-if="
              !isDesktop && data?.applications && data?.applications.length > 0
            "
            :data="data?.applications"
            :view="'dark'"
            :template="'hero'"
            class="hero-autocenter__applications"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-hero-autocenter-application'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.hero-autocenter {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 32px;
  @include media-breakpoint-up(sm) {
    gap: 24px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 24);
  }
  &__top {
    background: url() no-repeat center center/cover;
    display: grid;
    border-radius: 8px;
    height: calc(100vh - 88px);
    grid-template-columns: 1fr;
    grid-template-areas: 'text' 'info' 'socials';
    grid-template-rows: auto min-content min-content;
    max-height: 552px;
    padding: 16px;
    position: relative;
    @include media-breakpoint-up(sm) {
      grid-template-columns: auto fit-content(40%);
      grid-template-areas: 'text text' 'info socials';
      justify-content: space-between;
      padding: 16px 24px 24px;
      max-height: 576px;
      height: calc(100vh - 128px);
    }
    @include media-breakpoint-up(md) {
      grid-template-areas: 'text info' 'applications socials';
      max-height: 580px;
      height: calc(100vh - 176px);
      padding: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-height, 580);
      height: calc(100vh - px-to-vw(176));
      @include responsive-spacing(padding, 24);
      @include responsive-spacing(border-radius, 8);
    }
    &:before {
      background-color: rgba($black, 0.5);
      border-radius: 8px;
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 8);
      }
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
  }
  &__text {
    grid-area: text;
    position: relative;
    z-index: 1;
    @include media-breakpoint-up(sm) {
      max-width: 560px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-width, 560);
    }
  }
  &__buttons {
    align-items: flex-start;
    padding-top: 24px;
    z-index: 1;
    @include media-breakpoint-up(sm) {
      padding-top: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 40);
    }
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    grid-area: info;
    position: relative;
    margin-top: auto;
    z-index: 1;
    @include media-breakpoint-up(md) {
      margin-top: 0;
      text-align: right;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
    }
  }
  &__socials {
    grid-area: socials;
    padding-top: 16px;
    @include media-breakpoint-up(md) {
      align-items: flex-end;
      justify-content: flex-end;
      padding-top: 0;
    }
  }
  &__services {
    display: grid;
    gap: 8px;
    list-style: none;
    z-index: 1;
    @include media-breakpoint-up(sm) {
      align-items: end;
      grid-template-columns: repeat(3, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &-item {
      $parent: &;
      height: 100%;
      &-wrap {
        display: flex;
        flex-direction: column;
        position: relative;
        height: 100%;
      }
      &-top {
        align-items: center;
        background-color: $white;
        backdrop-filter: blur(20px);
        border-radius: 4px;
        display: grid;
        gap: 12px;
        grid-template-columns: 98px auto;
        min-height: 80px;
        padding: 4px 16px;
        width: 100%;
        @include media-breakpoint-up(sm) {
          grid-template-columns: 1fr;
          height: 100%;
          min-height: 120px;
          padding: 16px;
        }
        @include media-breakpoint-up(md) {
          grid-template-columns: 120px auto;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 4);
          @include responsive-spacing(gap, 12);
          @include responsive-spacing(min-height, 120);
          @include responsive-spacing(padding, 16);
          grid-template-columns: px-to-vw(120) auto;
        }
        &--with-description {
          @include media-breakpoint-up(md) {
            bottom: 0;
            position: absolute;
            transition: bottom 0.3s linear;
            padding: 16px;
            border-radius: 4px 4px 0 0;
          }
          @include media-breakpoint-up(xl) {
            border-top-left-radius: px-to-vw(4);
            border-top-right-radius: px-to-vw(4);
            padding: px-to-vw(16);
          }
        }
      }
      &-image {
        height: auto;
        width: 100%;
        @include media-breakpoint-up(sm) {
          margin: 0 auto auto;
          width: 58%;
        }
        @include media-breakpoint-up(md) {
          margin: 0;
          width: 100%;
        }
      }
      &-title {
        @include media-breakpoint-up(sm) {
          margin-top: auto;
        }
        @include media-breakpoint-up(md) {
          margin-top: 0;
        }
        &-value {
          @include h6-mobile;
          @include media-breakpoint-up(sm) {
            @include h6-desktop;
          }
        }
        &--with-arrow {
          align-items: center;
          display: grid;
          gap: 24px;
          grid-template-columns: auto 32px;
          @include media-breakpoint-up(sm) {
            align-items: end;
          }
          @include media-breakpoint-up(md) {
            align-items: center;
          }
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 24);
            grid-template-columns: auto px-to-vw(32);
          }
        }
      }
      &-icon {
        height: auto;
        margin: 0 auto;
        width: 50%;
        @include media-breakpoint-up(md) {
          &:deep(path) {
            stroke: $black;
          }
        }
      }
      &-description {
        display: none;
        @include media-breakpoint-up(md) {
          background-color: $white;
          border-radius: 0 0 4px 4px;
          border-top: 2px solid $divider;
          color: $on-surface-variant-2;
          display: flex;
          flex-direction: column;
          gap: 4px;
          height: 100%;
          padding: 32px 24px 24px;
          opacity: 0;
          transition:
            opacity 0.3s linear,
            visibility 0.3s linear;
          visibility: hidden;
        }
        &:deep(p) {
          margin-bottom: 0;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 4);
          border-bottom-left-radius: px-to-vw(4);
          border-bottom-right-radius: px-to-vw(4);
          border-top-width: px-to-vw(2);
          padding: px-to-vw(32) px-to-vw(24) px-to-vw(24);
        }
      }
      &:hover {
        #{$parent} {
          @include media-breakpoint-up(md) {
            &-top {
              border-radius: 4px 4px 0 0;
              &--with-description {
                bottom: 100%;
              }
            }
            &-icon {
              &:deep(path) {
                stroke: $primary;
              }
            }
            &-description {
              opacity: 1;
              visibility: visible;
            }
          }
        }
      }
    }
  }
  &__applications {
    grid-area: applications;
  }
}
</style>
